import type { AnimationDataV6 } from './types/v6';
import type { AnimationDataV7, ShotV7 } from './types/v7';

// GenX voiceovers were historically uploaded to the transient uploads bucket,
// which deletes objects 30 days after creation. A voiceover that aged past the
// window leaves a `voiceoverUrl` pointing at a deleted object. These hosts
// identify such doomed URLs.
const TRANSIENT_VOICEOVER_BUCKET_HOSTS = ['storage.lumen5.com', 'storage-dev.lumen5.com'];

function isTransientVoiceoverUrl(url: string | null): boolean {
    if (!url) {
        return false;
    }
    return TRANSIENT_VOICEOVER_BUCKET_HOSTS.some((host) => url.includes(`/${host}/`));
}

/**
 * V6 → V7. No shape change — this is a data fix. Voiceovers uploaded to the
 * transient uploads bucket (30-day deletion lifecycle) leave dead `voiceoverUrl`s
 * once they age out, breaking playback and rendering. This step nulls those
 * stale URLs (and their now-orphaned `transcriptWords`) on each affected shot so
 * the editor regenerates the audio into a permanent bucket. Shots whose
 * voiceover already lives in a permanent bucket — and shots with no voiceover —
 * pass through unchanged. Idempotent: once cleared (or once new voiceovers stop
 * landing in the transient bucket) there is nothing left to clear.
 */
export function v6ToV7(input: AnimationDataV6): AnimationDataV7 {
    const shots: ShotV7[] = input.shots.map((shot) =>
        isTransientVoiceoverUrl(shot.voiceoverUrl)
            ? { ...shot, voiceoverUrl: null, transcriptWords: null }
            : shot,
    );
    return { ...input, version: 7, shots };
}
